// Keeps the finished recording available across a page reload.
//
// The download used to be an object URL and nothing else, so refreshing the tab — or a crash, or a
// stray Ctrl+R — destroyed the only copy of a hearing that had just been recorded. localStorage
// cannot hold it (a three-hour file is ~130 MB against a 5 MB budget), so it goes to IndexedDB,
// which stores Blobs and is measured in gigabytes.
//
// The expiry is stored as an absolute timestamp rather than a remaining count, so time spent with
// the tab closed still counts against the 30 minutes. Reloading must not buy a fresh half hour.

const DB_NAME = "voxara-live-recording";
const DB_VERSION = 2;
const STORE = "pending";
const RECORD_KEY = "current";
// Chunks of a recording still in progress, plus the settings needed to finish it after a crash.
const CHUNK_STORE = "live-chunks";
const LIVE_META_STORE = "live-meta";
const LIVE_META_KEY = "current";

const openDb = () =>
  new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      // autoIncrement keeps the chunks in arrival order, which is the only order that decodes.
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(LIVE_META_STORE)) db.createObjectStore(LIVE_META_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const withStore = async (mode, run, storeName = STORE) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = run(tx.objectStore(storeName));
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

/**
 * @param record.blob          the copy the user downloads (MP3 when encoding succeeded)
 * @param record.uploadBlob    the webm the pipeline expects; dropped once it has been sent
 * @param record.expiresAt     absolute epoch ms, not a duration
 */
export async function savePendingRecording(record) {
  try {
    await withStore("readwrite", (store) =>
      store.put({ ...record, size: record.blob.size, uploaded: false, savedAt: Date.now() }, RECORD_KEY));
    return true;
  } catch (err) {
    // Quota, private browsing, a locked database: the in-memory copy still works for this tab.
    console.warn("Could not persist the recording for reload", err);
    return false;
  }
}

/** Records that the pipeline has the audio, and releases the second copy that was held for retry. */
export async function markPendingUploaded() {
  try {
    const existing = await withStore("readonly", (store) => store.get(RECORD_KEY));
    if (!existing) return;
    const { uploadBlob, ...rest } = existing;
    await withStore("readwrite", (store) => store.put({ ...rest, uploaded: true }, RECORD_KEY));
  } catch (err) {
    console.warn("Could not mark the stored recording as uploaded", err);
  }
}

export async function clearPendingRecording() {
  try {
    await withStore("readwrite", (store) => store.delete(RECORD_KEY));
  } catch (err) {
    console.warn("Could not clear the stored recording", err);
  }
}

/** Returns the stored recording, or null. Anything past its expiry is deleted rather than returned. */
export async function loadPendingRecording() {
  let record;
  try {
    record = await withStore("readonly", (store) => store.get(RECORD_KEY));
  } catch (err) {
    console.warn("Could not read the stored recording", err);
    return null;
  }
  if (!record || !record.blob) return null;

  if (!record.expiresAt || Date.now() >= record.expiresAt) {
    await clearPendingRecording();
    return { expired: true, name: record.name, size: record.size };
  }

  return {
    expired: false,
    blob: record.blob,
    name: record.name,
    size: record.size ?? record.blob.size,
    uploaded: Boolean(record.uploaded),
    uploadBlob: record.uploadBlob || null,
    uploadName: record.uploadName || record.name,
    language: record.language,
    recordingType: record.recordingType,
    secondsLeft: Math.max(0, Math.round((record.expiresAt - Date.now()) / 1000)),
  };
}


// ---------------------------------------------------------------------------
// A recording that is still running
// ---------------------------------------------------------------------------

/** Called once when recording starts, so a recovered file can be finished the way it was set up. */
export async function beginLiveRecording(meta) {
  try {
    await withStore("readwrite", (store) => store.clear(), CHUNK_STORE);
    await withStore("readwrite",
      (store) => store.put({ ...meta, startedAt: Date.now() }, LIVE_META_KEY), LIVE_META_STORE);
    return true;
  } catch (err) {
    console.warn("Could not start the crash-safe recording log", err);
    return false;
  }
}

/**
 * Appends one chunk. Deliberately fire-and-forget at the call site: a slow or failed write must
 * never stall the recorder, and the in-memory copy is still the primary one while the tab lives.
 */
export async function appendLiveChunk(blob) {
  try {
    await withStore("readwrite", (store) => store.add(blob), CHUNK_STORE);
    return true;
  } catch (err) {
    console.warn("Could not persist a recording chunk", err);
    return false;
  }
}

/** Drops the in-progress log. Called once the finished recording has been stored in its place. */
export async function clearLiveRecording() {
  try {
    await withStore("readwrite", (store) => store.clear(), CHUNK_STORE);
    await withStore("readwrite", (store) => store.delete(LIVE_META_KEY), LIVE_META_STORE);
  } catch (err) {
    console.warn("Could not clear the in-progress recording log", err);
  }
}

/**
 * Returns a recording that was cut off mid-way, reassembled, or null when there is nothing to
 * recover. Chunks shorter than a couple of seconds are discarded rather than offered: a webm
 * header with no audio behind it is not a recording, and offering it would waste the user's time.
 */
export async function recoverInterruptedRecording() {
  let meta = null;
  let chunks = [];
  try {
    meta = await withStore("readonly", (store) => store.get(LIVE_META_KEY), LIVE_META_STORE);
    chunks = await withStore("readonly", (store) => store.getAll(), CHUNK_STORE);
  } catch (err) {
    console.warn("Could not read the in-progress recording log", err);
    return null;
  }
  if (!meta || !chunks || chunks.length < 2) {
    if (meta) await clearLiveRecording();
    return null;
  }

  const mime = meta.mimeType || "audio/webm";
  const blob = new Blob(chunks, { type: mime });
  if (blob.size < 8 * 1024) {
    await clearLiveRecording();
    return null;
  }

  return {
    blob,
    mimeType: mime,
    startedAt: meta.startedAt,
    language: meta.language,
    recordingType: meta.recordingType,
    // Each chunk covers a fixed slice, so their count is a reliable duration without decoding.
    approximateSeconds: Math.round((chunks.length * (meta.timesliceMs || 4000)) / 1000),
  };
}
